import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ArrowLeft, Clock3, Gem, HelpCircle, History, Loader2, Trophy, Users } from 'lucide-react'
import HelpModal from '../../../components/HelpModal'
import { useTeamStore } from '../store/useTeamStore'
import TeamAvatar from '../components/TeamAvatar'
import { MODE_KINDS, modeIcon, modeLabel } from '../utils/teamStats'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import { hapticImpact } from '../../../utils/haptic'
import useAppStore from '../../../store/useAppStore'
import useAudioManager from '../../../hooks/useAudioManager'

const formatNumber = (value) => Number(value || 0).toLocaleString()

const formatCountdown = (value) => {
  const total = Math.max(0, Math.floor(Number(value) || 0))
  const hours = String(Math.floor(total / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0')
  const seconds = String(total % 60).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

const formatDate = (value, language) => {
  if (!value) return ''
  const date = new Date(`${value}T12:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' })
}

const TeamTopRow = ({ entry, place, prize, isMine, onOpen, t }) => {
  const Tag = onOpen ? 'button' : 'div'
  const rankClass = place === 1
    ? 'border-yellow-400/40 bg-yellow-400/10 text-yellow-300'
    : place === 2
      ? 'border-slate-300/30 bg-slate-300/10 text-slate-200'
      : place === 3
        ? 'border-orange-400/30 bg-orange-400/10 text-orange-300'
        : 'border-white/10 bg-white/5 text-text-secondary'

  return (
    <Tag
      onClick={onOpen}
      className={`grid w-full grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-2 rounded-xl border p-2.5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${isMine ? 'border-accent-primary/40 bg-accent-primary/10' : 'border-white/5 bg-dark-primary'} ${onOpen ? 'transition-all active:scale-[0.98]' : ''}`}
    >
      <span className={`flex h-7 w-7 items-center justify-center rounded-lg border text-xs font-black tabular-nums ${rankClass}`}>
        {place}
      </span>
      {entry ? (
        <div className="flex min-w-0 items-center gap-2.5">
          <TeamAvatar team={entry.team} size={38} />
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-white">{entry.team?.name}</p>
            <p className="truncate text-[11px] text-text-secondary">
              {entry.games == null
                ? `${formatNumber(entry.xp)} XP`
                : t('team.top.rowStats', '{{xp}} XP · {{games}} игр', {
                    xp: formatNumber(entry.xp),
                    games: formatNumber(entry.games)
                  })}
              {Number.isFinite(entry.level) ? ` · ${t('publicProfile.levelShort', 'LVL')} ${entry.level}` : ''}
            </p>
          </div>
        </div>
      ) : (
        <p className="text-xs text-text-secondary/60">{t('team.top.vacant', 'Место свободно')}</p>
      )}
      <span className="flex shrink-0 items-center gap-1 text-sm font-bold tabular-nums text-accent-primary">
        <Gem size={13} />
        {formatNumber(prize)}
      </span>
    </Tag>
  )
}

const TeamTop = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const {
    top, isTopLoading, topError, topHistory, isTopHistoryLoading, topHistoryError,
    loadTop, loadTopHistory
  } = useTeamStore()
  const myTeam = useAppStore((state) => state.user?.team)
  const isGuest = useAppStore((state) => state.isGuest)
  const minesEnabled = useAppStore((state) => state.user?.mines_enabled !== false)
  const { playSound } = useAudioManager({ pick: 'pick', select: 'select' })
  const [tab, setTab] = useState('today')
  const [mode, setMode] = useState('')
  const [remaining, setRemaining] = useState(0)
  const [isHelpOpen, setIsHelpOpen] = useState(false)

  useEffect(() => {
    loadTop()
    const poll = setInterval(loadTop, 60000)
    return () => clearInterval(poll)
  }, [loadTop, isGuest])

  useEffect(() => {
    setRemaining(Math.max(0, Number(top?.ends_in) || 0))
  }, [top?.ends_in, top?.date])

  useEffect(() => {
    const timer = setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (tab === 'history') loadTopHistory()
  }, [tab, loadTopHistory, isGuest])

  const availableModes = useMemo(() => {
    const values = new Set(top?.modes?.map((item) => item.mode))
    return MODE_KINDS.filter((kind) => values.has(kind) && (kind !== 'mines' || minesEnabled))
  }, [top, minesEnabled])

  useEffect(() => {
    if (availableModes.length > 0 && !availableModes.includes(mode)) setMode(availableModes[0])
  }, [availableModes, mode])

  if (!top && isTopLoading) {
    return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-accent-primary" /></div>
  }

  if (top?.enabled === false) return <Navigate to="/teams" replace />

  const currentMode = availableModes.includes(mode) ? top?.modes?.find((item) => item.mode === mode) : null
  const historyDays = availableModes.includes(mode) ? topHistory.days
    .map((day) => ({ date: day.date, data: day.modes?.find((item) => item.mode === mode) }))
    .filter((day) => day.data) : []
  const myOutsideTop = currentMode?.my?.place != null && !currentMode.top?.some((entry) => entry.place === currentMode.my.place)

  const selectTab = (value) => {
    if (value === tab) return
    playSound('pick')
    hapticImpact('light')
    setTab(value)
  }

  const selectMode = (value) => {
    if (value === mode) return
    playSound('pick')
    hapticImpact('light')
    setMode(value)
  }

  const openPath = (path) => {
    playSound('select')
    navigate(path)
  }

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-4xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="space-y-5">
        <motion.div variants={SECTION_VARIANT} className="flex items-center gap-3">
          <button onClick={() => openPath('/teams')} className="rounded-lg bg-surface p-2 text-white transition-all active:scale-[0.98]" aria-label={t('common.back', 'Назад')}>
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-2xl text-white">{t('team.top.title', 'Топ банд')}</h1>
              <button
                type="button"
                onClick={() => {
                  hapticImpact('light')
                  setIsHelpOpen(true)
                }}
                className="shrink-0 rounded-full bg-dark-secondary/50 p-1.5 text-text-secondary transition-colors hover:bg-dark-secondary active:scale-[0.98]"
                aria-label={t('team.top.help.button')}
              >
                <HelpCircle size={20} />
              </button>
            </div>
            <p className="hidden text-xs text-text-secondary">{t('team.top.subtitle', 'Ежедневный рейтинг по XP командных игр')}</p>
          </div>
          <Trophy size={24} className="shrink-0 text-yellow-300" />
        </motion.div>

        {topError ? (
          <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-8 text-center`}>
            <p className="mb-3 text-sm text-text-secondary">{t('team.top.loadError', 'Не удалось загрузить топ')}</p>
            <button onClick={loadTop} className="rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-5 py-2.5 text-sm font-bold text-dark-primary transition-all active:scale-[0.98]">
              {t('publicProfile.retry', 'Повторить')}
            </button>
          </motion.div>
        ) : top ? (
          <>
            <motion.div variants={SECTION_VARIANT} className="grid grid-cols-2 gap-2">
              <button onClick={() => selectTab('today')} className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-all active:scale-[0.98] ${tab === 'today' ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary' : 'border-white/10 bg-white/[0.03] text-text-secondary'}`}>
                <Trophy size={16} />{t('team.top.today', 'Сегодня')}
              </button>
              <button onClick={() => selectTab('history')} className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-all active:scale-[0.98] ${tab === 'history' ? 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary' : 'border-white/10 bg-white/[0.03] text-text-secondary'}`}>
                <History size={16} />{t('team.top.history', 'История')}
              </button>
            </motion.div>

            {availableModes.length > 0 && (
              <motion.div variants={SECTION_VARIANT} className="flex flex-wrap gap-2">
                {availableModes.map((kind) => {
                  const Icon = modeIcon(kind)
                  const active = mode === kind
                  return (
                    <button key={kind} onClick={() => selectMode(kind)} className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold transition-all active:scale-[0.98] ${active ? 'border-info/40 bg-info/10 text-info' : 'border-white/10 bg-white/[0.03] text-text-secondary'}`}>
                      <Icon size={14} />{modeLabel(kind, t)}
                    </button>
                  )
                })}
              </motion.div>
            )}

            {tab === 'today' && currentMode && (
              <>
                <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} grid grid-cols-3 gap-2 p-4`}>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-text-secondary">{t('team.top.fund', 'Фонд')}</p>
                    <p className="mt-1 flex items-center gap-1 text-sm font-bold tabular-nums text-accent-primary"><Gem size={13} />{formatNumber(currentMode.fund)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-text-secondary">{t('team.top.teams', 'Участников')}</p>
                    <p className="mt-1 flex items-center gap-1 text-sm font-bold tabular-nums text-white"><Users size={13} />{formatNumber(currentMode.teams_count)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-text-secondary">{t('team.top.endsIn', 'До итогов')}</p>
                    <p className="mt-1 flex items-center gap-1 text-sm font-bold tabular-nums text-white"><Clock3 size={13} />{formatCountdown(remaining)}</p>
                  </div>
                </motion.div>

                {!top.awarding && (
                  <motion.div variants={SECTION_VARIANT} className="rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                    {t('team.top.awardingTomorrow', 'Призы начнут начисляться завтра')}
                  </motion.div>
                )}

                <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-3`}>
                  <div className="space-y-2">
                    {currentMode.prizes.map((prize, index) => {
                      const place = index + 1
                      const entry = currentMode.top?.find((item) => item.place === place)
                      return <TeamTopRow key={place} entry={entry} place={place} prize={prize} isMine={currentMode.my?.place === place} onOpen={entry?.team?.slug ? () => openPath(`/teams/${entry.team.slug}`) : undefined} t={t} />
                    })}
                  </div>
                </motion.div>

                {myOutsideTop && (
                  <motion.div variants={SECTION_VARIANT} className="rounded-2xl border border-accent-primary/30 bg-accent-primary/10 p-3">
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-accent-primary">{t('team.top.myTeam', 'Моя банда')}</p>
                    <TeamTopRow entry={{ team: myTeam || { name: t('team.myTeam', 'Моя банда') }, xp: currentMode.my.xp }} place={currentMode.my.place} prize={currentMode.my.prize} isMine t={t} />
                  </motion.div>
                )}
              </>
            )}

            {tab === 'history' && (
              <>
                {isTopHistoryLoading && topHistory.days.length === 0 ? (
                  <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-accent-primary" /></div>
                ) : topHistoryError ? (
                  <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-8 text-center`}>
                    <p className="mb-3 text-sm text-text-secondary">{t('team.top.historyError', 'Не удалось загрузить историю')}</p>
                    <button onClick={() => loadTopHistory()} className="rounded-xl border border-accent-primary/30 bg-accent-primary/10 px-5 py-2.5 text-sm font-semibold text-accent-primary transition-all active:scale-[0.98]">{t('publicProfile.retry', 'Повторить')}</button>
                  </motion.div>
                ) : historyDays.length === 0 ? (
                  <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} p-8 text-center text-sm text-text-secondary`}>{t('team.top.historyEmpty', 'Итогов за последние 30 дней пока нет')}</motion.div>
                ) : (
                  <div className="space-y-4">
                    {historyDays.map(({ date, data }) => (
                      <motion.div key={date} variants={SECTION_VARIANT} className={`${CARD_CLASS} p-3`}>
                        <div className="mb-3 flex items-center justify-between gap-3 px-1">
                          <p className="font-bold text-white">{formatDate(date, i18n.resolvedLanguage)}</p>
                          <span className="flex items-center gap-1 text-xs font-semibold tabular-nums text-accent-primary"><Gem size={12} />{t('team.top.awarded', 'Выдано {{amount}}', { amount: formatNumber(data.awarded) })}</span>
                        </div>
                        {data.places?.length > 0 ? (
                          <div className="space-y-2">
                            {data.places.map((entry) => {
                              const teamSlug = entry.team?.slug || entry.team?.id
                              return <TeamTopRow key={entry.place} entry={entry} place={entry.place} prize={entry.prize} onOpen={teamSlug ? () => openPath(`/teams/${teamSlug}`) : undefined} t={t} />
                            })}
                          </div>
                        ) : (
                          <p className="rounded-xl border border-white/5 bg-dark-primary p-4 text-center text-xs text-text-secondary">{t('team.top.noGames', 'В этот день никто не играл командой')}</p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        ) : null}
      </motion.div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('team.top.help.title')}
        steps={[
          { title: t('team.top.help.ratingsTitle'), description: t('team.top.help.ratingsText') },
          { title: t('team.top.help.xpTitle'), description: t('team.top.help.xpText') },
          { title: t('team.top.help.roundsTitle'), description: t('team.top.help.roundsText') },
          { title: t('team.top.help.sessionsTitle'), description: t('team.top.help.sessionsText') },
          { title: t('team.top.help.placesTitle'), description: t('team.top.help.placesText') },
          { title: t('team.top.help.periodTitle'), description: t('team.top.help.periodText') },
          { title: t('team.top.help.rewardsTitle'), description: t('team.top.help.rewardsText') },
          { title: t('team.top.help.importantTitle'), description: t('team.top.help.importantText') }
        ]}
        highlightLastStep={false}
      />
    </div>
  )
}

export default TeamTop
